import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Target, TrendingDown, TrendingUp, X } from 'lucide-react';
import { getOfficerScorecard, getOfficerTrend, MetricBreakdownRow } from '../data/adminRoster';
interface OfficerScorecardModalProps {
  officerName: string | null;
  monthKey: string;
  onClose: () => void;
}
export function OfficerScorecardModal({
  officerName,
  monthKey,
  onClose
}: OfficerScorecardModalProps) {
  useEffect(() => {
    if (!officerName) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [officerName, onClose]);
  const scorecard = officerName ? getOfficerScorecard(officerName, monthKey) : null;
  return <AnimatePresence data-id="element-544" data-mp="1nsxh5xkjqqkr">
      {scorecard && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-end justify-center bg-gray-900/50 p-0 sm:items-center sm:p-6" onClick={onClose} role="presentation" data-id="element-545" data-mp="1nrnj38dp1ie4">
          <motion.div initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        duration: 0.18
      }} role="dialog" aria-modal="true" aria-label={`${scorecard.name} scorecard for ${scorecard.monthLabel}`} onClick={event => event.stopPropagation()} className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl" data-id="element-546" data-mp="1nk5i006fq8f7">
            <header className="flex shrink-0 items-start justify-between gap-4 bg-[#0a1e5e] px-5 py-4 text-white sm:px-6" data-id="element-547" data-mp="c5i9ln9eoe0v">
              <div className="min-w-0" data-id="element-548" data-mp="76ffuyyvlu87">
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-200" data-id="element-549" data-mp="75e04h6x0jk5">
                  {scorecard.monthLabel} scorecard
                </p>
                <h2 className="mt-0.5 truncate text-xl font-extrabold" data-id="element-550" data-mp="10vehckawcfc8">
                  {scorecard.name}
                </h2>
                <span className="mt-1.5 inline-block rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-100" data-id="element-551" data-mp="15q0k5z2wwzkp">
                  {scorecard.rank}
                </span>
              </div>
              <button type="button" onClick={onClose} className="rounded-lg bg-white/10 p-2 transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white" aria-label="Close scorecard" data-id="element-552" data-mp="hxjzrhvpcvi8">
                <X className="h-4 w-4" data-id="element-553" data-mp="1ycllnsd3qs1z" />
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6" data-id="element-554" data-mp="1yepl9y3yx4wg">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-555" data-mp="239lgt88eyaeb">
                <HeadlineStat label="Total points" value={scorecard.entry.points.toLocaleString()} tone={scorecard.entry.qualified ? 'green' : 'navy'} data-id="element-556" data-mp="3nsfgqe7toisg" />
                <HeadlineStat label="Monthly goal" value={scorecard.entry.goal.toLocaleString()} tone="navy" data-id="element-557" data-mp="3e230gy71jbs4" />
                <HeadlineStat label="Goal progress" value={`${scorecard.entry.goalPercent}%`} tone={scorecard.entry.qualified ? 'green' : 'amber'} data-id="element-558" data-mp="3540us5cd3uyi" />
                <HeadlineStat label="vs prior month" value={scorecard.entry.pointsDelta === null ? '—' : `${scorecard.entry.pointsDelta > 0 ? '+' : ''}${scorecard.entry.pointsDelta.toLocaleString()}`} tone={scorecard.entry.pointsDelta === null ? 'navy' : scorecard.entry.pointsDelta >= 0 ? 'green' : 'red'} data-id="element-559" data-mp="2v5bh6ori0wo2" />
              </div>

              <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4" data-id="element-560" data-mp="miy8i5l3stgj">
                <div className="flex items-center justify-between gap-3" data-id="element-561" data-mp="re3ut5if4a55">
                  <div className="flex items-center gap-1.5" data-id="element-562" data-mp="dnii5yjk4wfp">
                    <Target className="h-4 w-4 text-blue-600" data-id="element-563" data-mp="if0axzbffqpl" />
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-600" data-id="element-564" data-mp="igl7tvl7rlqm">
                      Bonus qualification
                    </p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${scorecard.entry.qualified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}`} data-id="element-565" data-mp="8pyuy94vt9bm">
                    {scorecard.entry.qualified ? 'Qualified' : 'Not qualified'}
                  </span>
                </div>
                <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-gray-200" data-id="element-566" data-mp="37a1wjxwzfrvc">
                  <div className={`h-full rounded-full ${scorecard.entry.qualified ? 'bg-green-500' : scorecard.entry.goalPercent >= 80 ? 'bg-amber-400' : 'bg-red-400'}`} style={{
                width: `${Math.min(100, scorecard.entry.goalPercent)}%`
              }} data-id="element-567" data-mp="3l8t574t2slc3" />
                </div>
                <p className="mt-2 text-xs font-semibold text-gray-500" data-id="element-568" data-mp="3q61t7m0wnjl9">
                  {scorecard.entry.qualified ? `${(scorecard.entry.points - scorecard.entry.goal).toLocaleString()} points above goal · ${scorecard.entry.unitsClosed} units closed` : `${(scorecard.entry.goal - scorecard.entry.points).toLocaleString()} points short of goal · ${scorecard.entry.unitsClosed} units closed`}
                </p>
              </div>

              <h3 className="mt-6 text-xs font-bold uppercase tracking-wider text-blue-600" data-id="element-569" data-mp="36mizld96erl9">
                Metric breakdown
              </h3>
              <div className="mt-2 overflow-hidden rounded-xl border border-gray-200" data-id="element-570" data-mp="3flw9g717pi6i">
                <table className="w-full border-collapse text-left" data-id="element-571" data-mp="3fk2zoivayhua">
                  <thead data-id="element-572" data-mp="3ke75uzwqulub">
                    <tr className="border-b border-gray-200 bg-gray-50" data-id="element-573" data-mp="3kegzqykwtd5a">
                      <th scope="col" className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-574" data-mp="3pb7bxatlfolf">
                        Metric
                      </th>
                      <th scope="col" className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-575" data-mp="76m9p5ksop1w">
                        Volume
                      </th>
                      <th scope="col" className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-576" data-mp="28zjy8p7jgvh1">
                        Per unit
                      </th>
                      <th scope="col" className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-577" data-mp="1rows2vygub2j">
                        Points
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-578" data-mp="2fe8yjo2es515">
                    {scorecard.rows.map(row => <MetricRow key={`${row.id}-${row.label}`} row={row} data-id="element-579" data-mp="2agpjwn1stq8o" />)}
                  </tbody>
                  <tfoot data-id="element-580" data-mp="yzhdxxbi822e">
                    <tr className="border-t border-gray-200 bg-gray-50" data-id="element-581" data-mp="yzpt5vv3njgr">
                      <td className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-600" colSpan={3} data-id="element-582" data-mp="uy4b7w3pkezp">
                        Points earned
                      </td>
                      <td className="px-4 py-3 text-right text-base font-extrabold text-[#0a1e5e]" data-id="element-583" data-mp="2z3a83mn7uzse">
                        {scorecard.earnedPoints.toLocaleString()}
                      </td>
                    </tr>
                    {scorecard.deductedPoints < 0 && <tr className="bg-red-50" data-id="element-584" data-mp="3dro8lqx5uc5s">
                        <td className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-red-700" colSpan={3} data-id="element-585" data-mp="3dszd5syfjnxy">
                          Deductions
                        </td>
                        <td className="px-4 py-2.5 text-right text-sm font-extrabold text-red-600" data-id="element-586" data-mp="38ylmg3upq4zm">
                          {scorecard.deductedPoints.toLocaleString()}
                        </td>
                      </tr>}
                  </tfoot>
                </table>
              </div>

              <h3 className="mt-6 text-xs font-bold uppercase tracking-wider text-blue-600" data-id="element-587" data-mp="enskvrhzvob5">
                12-month point history
              </h3>
              <TrendChart name={scorecard.name} activeMonth={monthKey} data-id="element-588" data-mp="ofy86nxaxhk0" />
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function HeadlineStat({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone: 'navy' | 'green' | 'amber' | 'red';
}) {
  const toneClass = {
    navy: 'text-[#0a1e5e]',
    green: 'text-green-600',
    amber: 'text-amber-600',
    red: 'text-red-600'
  }[tone];
  return <div className="rounded-lg border border-gray-200 bg-white p-3" data-id="element-589" data-mp="5muckx0iwumr">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-590" data-mp="p2n09ncpklq">
        {label}
      </p>
      <p className={`mt-1 text-xl font-extrabold ${toneClass}`} data-id="element-591" data-mp="1h2izjods1tnk">{value}</p>
    </div>;
}
function MetricRow({
  row
}: {
  row: MetricBreakdownRow;
}) {
  const negative = row.points < 0;
  return <tr data-id="element-592" data-mp="cs2yawj35yez">
      <td className="px-4 py-3" data-id="element-593" data-mp="csmdw6l9kapu">
        <p className="text-sm font-bold text-gray-800" data-id="element-594" data-mp="7ub2vsj8fu61">{row.label}</p>
        <p className="mt-0.5 text-[11px] leading-snug text-gray-500" data-id="element-595" data-mp="7v2yh3ctxrwb">
          {row.detail}
        </p>
        <span className="mt-1 inline-block rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-500" data-id="element-596" data-mp="31i03a7bqvwl">
          {row.category}
        </span>
      </td>
      <td className="px-4 py-3 text-right text-sm font-semibold text-gray-700" data-id="element-597" data-mp="3q92758360v51">
        {row.volumeLabel}
      </td>
      <td className="px-4 py-3 text-right text-xs font-semibold text-gray-500" data-id="element-598" data-mp="3l7wbb1e7krra">
        {row.pointsEach === null ? 'Tiered' : `${row.pointsEach > 0 ? '+' : ''}${row.pointsEach.toLocaleString()}`}
      </td>
      <td className={`px-4 py-3 text-right text-sm font-extrabold ${negative ? 'text-red-600' : row.points === 0 ? 'text-gray-400' : 'text-green-600'}`} data-id="element-599" data-mp="3bde2jhtxq60m">
        {row.points > 0 ? '+' : ''}
        {row.points.toLocaleString()}
      </td>
    </tr>;
}
function TrendChart({
  name,
  activeMonth
}: {
  name: string;
  activeMonth: string;
}) {
  const trend = getOfficerTrend(name);
  const max = Math.max(...trend.map(point => point.points), 1);
  return <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4" data-id="element-600" data-mp="1qtr41y5q0eff">
      <div className="flex h-28 items-end gap-1.5" data-id="element-601" data-mp="1vqr15jkle6ue">
        {trend.map(point => {
        const active = point.monthKey === activeMonth;
        return <div key={point.monthKey} className="flex min-w-0 flex-1 flex-col items-center gap-1.5" data-id="element-602" data-mp="1334w06vpuu5f">
              <span className="text-[9px] font-bold text-gray-400" data-id="element-603" data-mp="180wp3483kmb7">
                {(point.points / 1000).toFixed(1)}k
              </span>
              <div title={`${point.label}: ${point.points.toLocaleString()} points`} className={`w-full rounded-t ${active ? 'bg-[#0a1e5e]' : 'bg-blue-200'}`} style={{
            height: `${Math.max(6, point.points / max * 72)}px`
          }} data-id="element-604" data-mp="2jhn1j5346ad9" />
              <span className={`text-[10px] font-bold ${active ? 'text-[#0a1e5e]' : 'text-gray-400'}`} data-id="element-605" data-mp="1yuf12baov7wr">
                {point.label}
              </span>
            </div>;
      })}
      </div>
      <div className="mt-3 flex items-center gap-4 border-t border-gray-100 pt-3 text-[11px] font-semibold text-gray-500" data-id="element-606" data-mp="23r5d8njdhjcw">
        <span className="flex items-center gap-1.5" data-id="element-607" data-mp="1g2vz87sehzwb">
          <TrendingUp className="h-3.5 w-3.5 text-green-600" data-id="element-608" data-mp="1b315lwh8qw7k" />
          Best {Math.max(...trend.map(p => p.points)).toLocaleString()}
        </span>
        <span className="flex items-center gap-1.5" data-id="element-609" data-mp="1ptgv3ngowqbj">
          <TrendingDown className="h-3.5 w-3.5 text-red-500" data-id="element-610" data-mp="1l1gz263qqhk8" />
          Low {Math.min(...trend.map(p => p.points)).toLocaleString()}
        </span>
      </div>
    </div>;
}